import { GenericLazyString, gettext } from '@eventbrite/legacy-infra.i18n';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import React, { useEffect, useState } from 'react';
import {
    GA_ACTION_FORM_DISMISSED,
    GA_REPORT_EVENT_CATEGORY,
    SUCCESS_VIEW,
} from '../constants';
import { Pages } from '../types';
import { usePaginationDisplayContext } from './Pagination';

const defaultConfig: OverlayConfigOptions = {
    title: gettext('Report This Event'),
};

interface OverlayConfigOptions {
    title: GenericLazyString;
    onPrimaryAction?: () => void;
    primaryText?: string;
    primaryType?: string;
    onSecondaryAction?: () => void;
    secondaryText?: string;
    secondaryType?: string;
}

interface OverlayConfig {
    overlayConfig: OverlayConfigOptions;
    setOverlayConfig: (config: OverlayConfigOptions) => void;
}

interface OverlayVisible {
    isVisible: boolean;
    setVisible: (options?: { value?: boolean; noAnalyticsTrack?: boolean }) => void;
    setCurrentView: (view: string) => void;
}

export const OverlayConfigContext = React.createContext<Partial<OverlayConfig>>(
    {},
);
export const OverlayVisibilityContext = React.createContext<
    Partial<OverlayVisible>
>({});

interface OverlayProviderProps {
    isVisible: boolean;
    onClose: () => void;
    showOverlay?: () => void;
}

export const OverlayProvider = (
    props,
) => {
    const [isVisible, setVisible] = useState(props.isVisible);
    const [currentView, setCurrentView] = useState<string | null>(null);
    const [overlayConfig, setOverlayConfig] = useState(defaultConfig);
    const { setPage } = usePaginationDisplayContext();

    useEffect(() => {
        setVisible(props.isVisible);
        if (props.isVisible) setPage?.(Pages.intro);
    }, [props.isVisible, setPage]);

    const handleSetVisible = ({
        value = false,
        noAnalyticsTrack = false,
    } = {}) => {
        setVisible(value);

        if (!value && props.onClose) {
            props.onClose();
        }

        if (!value && currentView !== SUCCESS_VIEW && !noAnalyticsTrack) {
            trackAnalyticsEvent({
                action: GA_ACTION_FORM_DISMISSED,
                category: GA_REPORT_EVENT_CATEGORY,
                label: currentView,
            });
        }
    };

    return (
        <OverlayConfigContext.Provider
            value={{ overlayConfig, setOverlayConfig }}
        >
            <OverlayVisibilityContext.Provider
                value={{
                    isVisible,
                    setVisible: handleSetVisible,
                    setCurrentView: (view: string) => setCurrentView(view),
                }}
            >
                {props.children}
            </OverlayVisibilityContext.Provider>
        </OverlayConfigContext.Provider>
    );
};
